import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { ASSETS } from "../lib/assets";
import { Reveal, MaskedLine, GoldLine, Eyebrow } from "../components/Reveal";

const PageHero = ({ title, sub }) => (
  <section className="bg-ivory pb-16 pt-36 lg:pt-44">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <MaskedLine>
        <h1 className="font-heading text-5xl font-semibold leading-none text-navy sm:text-6xl lg:text-7xl">{title}</h1>
      </MaskedLine>
      <GoldLine className="mt-8 w-20" delay={0.3} />
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mt-6 max-w-2xl text-base font-light leading-relaxed text-ink/70 md:text-lg"
      >
        {sub}
      </motion.p>
    </div>
  </section>
);

export default function About() {
  const { t } = useLang();
  const a = t.about;

  return (
    <>
      <PageHero title={a.title} sub={a.sub} />

      <section className="bg-ivory pb-24 lg:pb-32" data-testid="about-school">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-2">
            <span className="font-heading text-6xl font-medium text-gold/50">01</span>
            <Eyebrow className="mt-3">{a.schoolEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1} className="min-w-0 lg:col-span-5">
            <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{a.schoolTitle}</h2>
            <p className="mt-8 text-base font-light leading-relaxed text-ink/75 md:text-lg">{a.schoolBody1}</p>
            <p className="mt-5 text-base font-light leading-relaxed text-ink/75 md:text-lg">{a.schoolBody2}</p>
            <p className="mt-8 border-l-2 border-gold pl-5 font-heading text-xl font-medium italic leading-snug text-navy">
              {a.resultsLine}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="min-w-0 lg:col-span-5">
            <div className="relative">
              <span className="absolute -left-3 -top-3 h-full w-full border border-gold/50" aria-hidden="true" />
              <img src={ASSETS.building} alt="SMHSS school building" className="relative h-80 w-full object-cover sm:h-[480px]" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy-deep py-24 text-ivory lg:py-32" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-2">
            <span className="font-heading text-6xl font-medium text-gold/60">02</span>
            <Eyebrow light className="mt-3">{a.storyEyebrow}</Eyebrow>
          </Reveal>
          <div className="min-w-0 lg:col-span-7">
            <Reveal>
              <h2 className="font-heading text-4xl font-semibold leading-tight md:text-5xl">{a.storyTitle}</h2>
              <GoldLine className="mt-8 w-16" />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">{a.storyP1}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">{a.storyP2}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">{a.storyP3}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="min-w-0 lg:col-span-3">
            <div className="relative overflow-hidden">
              <img src={ASSETS.corridor} alt="SMHSS entrance corridor" className="h-72 w-full object-cover lg:h-96" />
              <span className="absolute inset-0 border border-gold/30" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmneutral py-24 lg:py-32" data-testid="about-philosophy">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-2">
            <span className="font-heading text-6xl font-medium text-gold/50">03</span>
            <Eyebrow className="mt-3">{a.philEyebrow}</Eyebrow>
          </Reveal>
          <div className="min-w-0 lg:col-span-10">
            <Reveal>
              <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{a.philTitle}</h2>
              <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-ink/75 md:text-lg">{a.philBody}</p>
            </Reveal>
            <div className="mt-12 grid gap-px bg-gold/30 sm:grid-cols-3">
              {t.philosophy.map((p, i) => (
                <Reveal key={i} delay={i * 0.12} className="bg-warmneutral p-8">
                  <span className="text-xs tracking-[0.3em] text-gold">{p.num}</span>
                  <h3 className="mt-3 font-heading text-2xl font-semibold text-navy">{p.word}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-ink/70">{p.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-32" data-testid="about-leadership">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-2">
            <span className="font-heading text-6xl font-medium text-gold/50">04</span>
            <Eyebrow className="mt-3">{a.leadershipEyebrow}</Eyebrow>
          </Reveal>
          <div className="min-w-0 lg:col-span-10">
            <Reveal>
              <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{a.leadershipTitle}</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {[
                { role: a.chairperson, name: a.chairpersonName, note: a.chairpersonNote, id: "chairperson" },
                { role: a.principal, name: a.principalName, note: a.principalNote, id: "principal" },
              ].map((p, i) => (
                <Reveal key={p.id} delay={i * 0.12} data-testid={`leader-${p.id}`}>
                  <div className="group border border-navy/10 p-8 transition-colors duration-500 hover:border-gold lg:p-10">
                    <span className="text-xs uppercase tracking-[0.3em] text-gold">{p.role}</span>
                    <h3 className="mt-4 font-heading text-3xl font-semibold text-navy">{p.name}</h3>
                    <span className="mt-4 block h-px w-8 bg-gold transition-[width] duration-500 group-hover:w-16" aria-hidden="true" />
                    <p className="mt-5 text-sm font-light leading-relaxed text-ink/70">{p.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15} className="mt-16">
              <h3 className="font-heading text-2xl font-semibold text-navy md:text-3xl">{a.membersTitle}</h3>
              <span className="mt-4 block h-px w-12 bg-gold" aria-hidden="true" />
              <div className="mt-8">
                {a.members.map((m, i) => (
                  <div
                    key={i}
                    data-testid={`society-member-${i}`}
                    className="group grid grid-cols-12 items-baseline gap-4 border-b border-navy/10 py-4 transition-colors duration-300 hover:border-gold"
                  >
                    <span className="col-span-2 font-heading text-xl font-medium text-gold/60 sm:col-span-1">
                      0{i + 1}
                    </span>
                    <span className="col-span-10 min-w-0 break-words font-heading text-xl font-medium text-navy sm:col-span-6 md:text-2xl">
                      {m.name}
                    </span>
                    <span className="col-span-10 col-start-3 min-w-0 break-words text-xs uppercase tracking-[0.2em] text-ink/50 sm:col-span-5 sm:col-start-auto sm:text-right md:text-sm">
                      {m.role}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-gold/25 bg-cream py-20 lg:py-24" data-testid="about-society">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-2">
            <span className="font-heading text-6xl font-medium text-gold/50">05</span>
            <Eyebrow className="mt-3">{a.societyEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1} className="min-w-0 lg:col-span-7">
            <h2 className="font-heading text-3xl font-semibold text-navy md:text-4xl">{a.societyTitle}</h2>
            <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-ink/75 md:text-lg">{a.societyBody}</p>
          </Reveal>
          <Reveal delay={0.2} className="min-w-0 lg:col-span-3 lg:justify-self-end">
            <img src={ASSETS.logo} alt="SMHSS crest" className="h-28 w-auto lg:h-36" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
